import React, { Fragment, ReactNode, useCallback, useMemo } from "react";
import {
  Button,
  Divider,
  Flex,
  FlexProps,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";
import UserAvatar from "modules/_shared/user-avatar";
import { useAuth } from "modules/providers/auth-provider";
import UserName from "modules/_shared/user-name";
import selectedNavLinkProps from "modules/messaging/left-nav/selected-nav-link-props";
import { useRouter } from "next/router";
import Link from "modules/layouts/_shared/link";
import {
  ArrowIcon,
  AdminTagCategoriesIcon,
  HashIcon,
  AdminTextIcon,
  PersonOutlineIcon,
  LogoIcon,
  SupportIcon,
  PeopleOutlineIcon,
  FolderPersonIcon,
  LogoTextIcon,
} from "modules/icons";
import { HOME_PATH } from "modules/utils/Constants";
import useStickyFilter from "modules/hooks/use-sticky-filter";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps extends FlexProps {}

export const AdminLeftNav = (props: IProps) => {
  const { me } = useAuth();
  const { pathname } = useRouter();
  const { openDrawer } = useOverlay();

  const usersSelected = useMemo(() => pathname === "/admin/users", [pathname]);

  const groupsSelected = useMemo(
    () => pathname === "/admin/groups",
    [pathname],
  );

  const tagsSelected = useMemo(() => pathname === "/admin/tags", [pathname]);

  const tagCategoriesSelected = useMemo(
    () => pathname === "/admin/tag-categories",
    [pathname],
  );

  const userAttributesCategoriesSelected = useMemo(
    () => pathname === "/admin/user-attributes-categories",
    [pathname],
  );

  const openSupport = useCallback(() => {
    openDrawer({ type: DRAWER_TYPE.SUPPORT });
  }, [openDrawer]);

  const { url: homeUrl } = useStickyFilter({
    path: HOME_PATH,
  });

  const children = useMemo(() => {
    if (!me) return [];
    const links: JSX.Element[] = [];
    links.push(
      <HStack
        spacing={2}
        px="6px"
        py={2}
        key="left-nav-admin-user"
        bg={colors.black}
        w="full"
      >
        <UserAvatar userId={me.id} size="xs" />
        <Text noOfLines={1}>
          <UserName userId={me.id} />
        </Text>
      </HStack>,
    );
    links.push(<NavDivider key="left-nav-divider-1" />);
    links.push(
      <NavLink
        key="left-nav-users"
        icon={<PersonOutlineIcon boxSize={4} fill={colors.grayPrimary} />}
        title="Users"
        href="/admin/users"
        selected={usersSelected}
      />,
    );
    links.push(
      <NavLink
        key="left-nav-groups"
        icon={<PeopleOutlineIcon boxSize={4} fill={colors.grayPrimary} />}
        title="Groups"
        href="/admin/groups"
        selected={groupsSelected}
      />,
    );
    links.push(
      <NavLink
        key="left-nav-tags"
        icon={<HashIcon boxSize={4} fill={colors.grayPrimary} />}
        title="Tags"
        href="/admin/tags"
        selected={tagsSelected}
      />,
    );
    links.push(
      <NavLink
        key="left-nav-tag-categories"
        icon={<AdminTagCategoriesIcon boxSize={4} fill={colors.grayPrimary} />}
        title="Tag Categories"
        href="/admin/tag-categories"
        selected={tagCategoriesSelected}
      />,
    );
    links.push(<NavDivider key="left-nav-divider-2" />);
    links.push(
      <NavLink
        key="user-attributes-categories"
        icon={<FolderPersonIcon boxSize={4} fill={colors.grayPrimary} />}
        title="User Attribute Categories"
        href="/admin/user-attributes-categories"
        selected={userAttributesCategoriesSelected}
      />,
    );
    links.push(<NavDivider key="left-nav-divider-2" />);
    links.push(
      <Button
        key="left-nav-support"
        variant="link"
        alignSelf="flex-start"
        pl={5}
        py={1}
        onClick={openSupport}
        leftIcon={<SupportIcon boxSize={4} fill={colors.grayPrimary} />}
      >
        Support
      </Button>,
    );
    links.push(<NavDivider key="left-nav-divider-2" />);
    links.push(
      <NavLink
        key="left-nav-tag-back"
        icon={<ArrowIcon boxSize={4} fill={colors.grayPrimary} />}
        title="Back to Synco"
        href={homeUrl}
      />,
    );
    return links;
  }, [
    groupsSelected,
    homeUrl,
    me,
    openSupport,
    tagCategoriesSelected,
    tagsSelected,
    userAttributesCategoriesSelected,
    usersSelected,
  ]);

  return (
    <Flex
      bg={colors.blackTertiary}
      as="nav"
      overflowY="auto"
      height="full"
      w={280}
      direction="column"
      justify="space-between"
      {...props}
      color={colors.grayPrimary}
    >
      <Stack spacing={0} pb={4} pt={4}>
        <Flex
          position="sticky"
          top={0}
          pl={4}
          pr={4}
          py={3}
          align="center"
          zIndex={100}
          bg={colors.blackTertiary}
        >
          <HStack spacing={2} pl={2} pb={3}>
            <LogoIcon boxSize={7} />
            <LogoTextIcon mb={1} fill={colors.grayPrimary} />
            <AdminTextIcon fill={colors.grayPrimary} />
          </HStack>
        </Flex>
        <VStack spacing={2} px={4} alignItems="flex-start">
          {children.map((child, index) => (
            <Fragment key={`nav-child-${index}`}>{child}</Fragment>
          ))}
        </VStack>
      </Stack>
    </Flex>
  );
};

const NavDivider = () => <Divider borderColor={colors.gray50} />;

const NavLink = ({
  icon,
  title,
  href,
  selected,
}: {
  icon: ReactNode;
  title: string;
  href: string;
  selected?: boolean;
  unreadCount?: number;
}) => (
  <Link
    href={href}
    w="full"
    pl={5}
    pr={1}
    py={1}
    {...(selected && selectedNavLinkProps)}
  >
    <HStack>
      {icon}
      <Text flex={1}>{title}</Text>
    </HStack>
  </Link>
);
